"use client";

import { useState } from "react";
import { Link2, Video } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { useStorage } from "@/features/storage/useStorage";
import { EmbedBlock } from "@/lib/utils/lomdaV3/types";
import { openStorageVideoPicker } from "../../lib/storageVideoPicker";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import {
    PopoverTitle,
    ToolbarDivider,
    WidthControl,
    chromeBtn,
    chromeBtnOpen,
    popoverShell,
} from "./blockAppearance";
import { EmbedBlockedWarning } from "./embedBlockedWarning";

/**
 * Embed topbar: Width + a replace control. Replaces the in-block replace
 * buttons — media blocks are driven from the topbar now. Storage embeds hold a
 * file id rather than a url, so they get the storage picker instead of a link
 * input.
 */
export function EmbedSettings({ block }: { block: EmbedBlock }) {
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);
    const setExplorerOpen = useStorage((s) => s.setExplorerOpen);
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState("");

    const isStorage = block.data.provider === "storage";

    const submit = () => {
        const trimmed = value.trim();
        if (!trimmed) return;
        updateBlock(block.id, { url: trimmed });
        setValue("");
        setOpen(false);
    };

    return (
        <div className="flex items-center gap-3">
            <WidthControl blockId={block.id} />
            {block.data.url && isStorage && (
                <>
                    <ToolbarDivider />
                    <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() =>
                            openStorageVideoPicker(setExplorerOpen, (videoId) =>
                                updateBlock(block.id, { url: videoId })
                            )
                        }
                        className={chromeBtn}
                    >
                        <Video className="size-5" />
                        {t("lomda-v3-editor.media.replace-video")}
                    </button>
                </>
            )}
            {block.data.url && !isStorage && (
                <>
                    <ToolbarDivider />
                    <Popover
                        open={open}
                        onOpenChange={(o) => {
                            setOpen(o);
                            if (o) setValue(block.data.url); // prefill current url
                        }}
                    >
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                className={cn(chromeBtn, chromeBtnOpen)}
                            >
                                <Link2 className="size-5" />
                                {t("lomda-v3-editor.embed.replace-url")}
                            </button>
                        </PopoverTrigger>
                        <PopoverContent
                            align="start"
                            className={cn("w-[360px]", popoverShell)}
                        >
                            <div className="flex flex-col gap-4">
                                <PopoverTitle>
                                    {t("lomda-v3-editor.embed.replace-url")}
                                </PopoverTitle>
                                <div className="flex gap-2">
                                    <Input
                                        value={value}
                                        onChange={(e) =>
                                            setValue(e.target.value)
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") submit();
                                        }}
                                        placeholder={t(
                                            "lomda-v3-editor.embed.paste"
                                        )}
                                        dir="ltr"
                                        className="h-9"
                                        autoFocus
                                    />
                                    <Button
                                        type="button"
                                        size="sm"
                                        disabled={!value.trim()}
                                        onClick={submit}
                                    >
                                        {t("lomda-v3-editor.embed.submit")}
                                    </Button>
                                </div>
                                <EmbedBlockedWarning
                                    provider={block.data.provider}
                                    value={value}
                                />
                            </div>
                        </PopoverContent>
                    </Popover>
                </>
            )}
        </div>
    );
}
